import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";

const FAQS = [
  {
    q: "What is STR Surplus?",
    a: "STR Surplus is a liquidation and auction-lot marketplace built for short-term rental owners, operators, and property managers. We source surplus, customer-returned, and auction-lot inventory — the same wholesale channels as B-Stock — and resell it below list price with clear condition grading, so you can restock Airbnb and vacation rental properties for less.",
  },
  {
    q: "How do liquidation and auction lots work?",
    a: "Manufacturers and big-box retailers offload overstock, shelf-pulls, and customer returns in bulk pallets and lots. We buy those lots, inspect and grade each item, then list them individually or as bundled Lot Buys. Because we buy at liquidation prices, we can pass steep discounts to you. Quantities are limited and one-of-a-kind — when a unit sells, it's gone.",
  },
  {
    q: "What do the condition grades mean?",
    a: "Every item is graded so you know exactly what you're getting: New / Brand New / In Box (NIB) is unused in original packaging; Like New / Mint is opened but shows no wear; New Other / Scratch & Dent is functionally new with cosmetic marks; Floor Model / Display was showroom-used; Good / Fair (Tested & Working) shows normal use and has been tested; Used / Pre-owned (Tested & Working) is pre-owned and verified functional; As-Is / Untested / Salvage is sold for parts or repair with no guarantee.",
  },
  {
    q: "How is shipping handled, and do you offer local pickup?",
    a: "We ship via UPS Ground nationwide from Fort Lauderdale, Florida — shipping is calculated at checkout by weight. If you're local, you can select free local pickup in Fort Lauderdale instead of paying for shipping.",
  },
  {
    q: "How do I pay?",
    a: "Checkout is handled securely by Stripe. You can pay as a guest with any major credit or debit card — no account required. Florida sales tax is applied where applicable.",
  },
  {
    q: "Are these items covered by a warranty or returns?",
    a: "Items are sold in surplus/liquidation condition at the grade shown on each listing. Manufacturer warranties may not apply. Because inventory is limited and one-of-a-kind, please review the condition grade, photos, and any notes before purchasing. Reach out before buying if you have questions about a specific unit.",
  },
  {
    q: "Who should buy from STR Surplus?",
    a: "Short-term rental owners and hosts (Airbnb, VRBO, Booking.com), STR co-hosts and operators managing multiple units, property and hospitality managers, and hotel/FF&E buyers looking to restock or furnish rentals affordably with small appliances, coffee makers, kitchenware, linens, and electronics.",
  },
  {
    q: "Can I buy in bulk for multiple properties?",
    a: "Yes. Check the Lot Buys page for bundled deals that group multiple units at a deeper discount — ideal for operators outfitting several rentals at once. New lots are added regularly as fresh inventory arrives.",
  },
];

export default function FAQ() {
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="max-w-[900px] mx-auto px-4 sm:px-8 py-12">
      <Seo
        title="FAQ — Liquidation Lots, Conditions & Shipping"
        description="How STR Surplus liquidation and auction lots work — condition grades, UPS Ground shipping, local pickup, payment, and who it's for. Answers for short-term rental owners, operators and managers."
        path="/faq"
        jsonLd={faqLd}
      />
      <div className="font-mono text-[11px] uppercase tracking-widest text-subtle">Support</div>
      <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-2">Frequently Asked Questions</h1>
      <p className="font-mono text-sm text-subtle mt-3 max-w-2xl">Liquidation and auction-lot deals for short-term rental owners, operators, and managers. Here's how it works.</p>

      <div className="mt-10 border-t-2 border-ink" data-testid="faq-list">
        {FAQS.map((f, i) => (
          <div key={i} className="border-b border-ink/20 py-6" data-testid={`faq-item-${i}`}>
            <h2 className="font-heading font-black text-lg sm:text-xl tracking-tight flex gap-3">
              <span className="font-mono text-subtle text-sm shrink-0 pt-1">{String(i + 1).padStart(2, "0")}</span>
              <span>{f.q}</span>
            </h2>
            <p className="font-mono text-sm leading-relaxed text-subtle mt-3 sm:pl-9">{f.a}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 flex flex-wrap gap-3 font-mono text-[11px] uppercase tracking-widest">
        <Link to="/" className="border border-ink px-4 h-10 flex items-center hover:bg-ink hover:text-paper transition-colors" data-testid="faq-shop-link">Shop the Catalog</Link>
        <Link to="/lot-buys" className="border border-ink px-4 h-10 flex items-center hover:bg-ink hover:text-paper transition-colors">Browse Lot Buys</Link>
      </div>
    </div>
  );
}
